import { useAppSelector, useAppDispatch } from 'hooks/reduxHooks'
import { fetchProductSubscription } from 'actions/LuxPlusActions'
import LuxPlusLandingPageContent from 'luxPlus/components/LuxPlusLandingPageContent'
import React, { useCallback, useContext, useEffect } from 'react'
import { useIntl } from 'react-intl'
import LuxPlusMonthLandingPageContent from 'takeovers/luxPlusMonthTakeover/components/LuxPlusMonthLandingPageContent'
import { LUX_PLUS_MONTH_TAKEOVER_ID } from 'takeovers/luxPlusMonthTakeover/constants/luxPlusMonthTakeoverConstants'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { selectLoggedIn } from 'selectors/accountSelectors'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import ModalContext from 'contexts/ModalContext'
import purchaseLuxPlus from 'actions/luxPlus/purchaseLuxPlus'
import useQueryParams from 'hooks/useQueryParams'
import { LUXURY_PLUS, LUX_PLUS_PROMOTIONAL_OFFER_PARAM, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { replaceWithRegion } from 'actions/NavigationActions'
import getLuxPlusShouldRedirectToAccountPage from 'luxPlus/selectors/getLuxPlusShouldRedirectToAccountPage'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'

function LuxPlusLandingPage() {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const queryParams = useQueryParams()

  useEffect(() => {
    dispatch(fetchProductSubscription())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const shouldRedirectToAccountPage = useAppSelector(getLuxPlusShouldRedirectToAccountPage)

  // Show snackbar if user is on landing page with a benefit param but can't access it
  // (they were likely redirected from the account page or clicked a benefit link)
  useEffect(() => {
    const hasBenefitParam = queryParams.has('benefit')
    if (hasBenefitParam && !shouldRedirectToAccountPage) {
      showSnackbar(
        intl.formatMessage(
          {
            id: 'TiF8HQ2vl1Zz7f3zdv_4',
            defaultMessage: 'Become a full {programName} member to access partner-only deals and benefits.',
            description: 'Snackbar message shown when a non-member tries to access a LuxPlus benefit from the landing page',
          },
          { programName: LUXURY_PLUS.PROGRAM_NAME },
        ),
        'neutral',
        {
          heading: intl.formatMessage({
            id: 'LCD0nXWOQKq9-GIgbd8C',
            defaultMessage: 'Membership required',
            description: 'Snackbar heading when a non-member tries to access a LuxPlus benefit',
          }),
        },
      )
    }
  }, [queryParams, shouldRedirectToAccountPage, intl])
  useEffect(() => {
    if (!shouldRedirectToAccountPage) return
    // Preserve query params (e.g., orderId from post-purchase redirect)
    const queryString = window.location.search
    dispatch(replaceWithRegion(`/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}${queryString}`))
  }, [dispatch, shouldRedirectToAccountPage])

  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const currentTakeover = useCurrentSiteTakeover()

  const isLoggedIn = useAppSelector(selectLoggedIn)
  const showModal = useContext(ModalContext)
  const openLoginModal = useCallback(() => showModal<AccountAccessModalResult>(<AccountAccessModal />), [showModal])

  const isLuxPlusMember = useAppSelector(getCanRedeemLuxPlusBenefits)
  const hasPromotionParam = queryParams.get(LUX_PLUS_PROMOTIONAL_OFFER_PARAM) === 'true'

  const isLuxPlusPromotionalOfferEnabled = !isLuxPlusMember && hasPromotionParam

  const purchasePromotionalLuxPlusOffer = useCallback(() => {
    if (hasPromotionParam && !isLoggedIn) {
      openLoginModal()
      return
    }
    if (!isLuxPlusPromotionalOfferEnabled) return
    dispatch(purchaseLuxPlus({ hasJoinFee: false, replaceCurrentWith: `/${LUXURY_PLUS_PATHS.DEFAULT_LANDING_PAGE}` }))
  }, [hasPromotionParam, isLuxPlusPromotionalOfferEnabled, isLoggedIn, openLoginModal, dispatch])

  const subscriptionProduct = useAppSelector(state => state.luxPlus.products.subscription)

  useEffect(() => {
    purchasePromotionalLuxPlusOffer()
  }, [purchasePromotionalLuxPlusOffer, subscriptionProduct.offers])

  if (!isSpoofed && currentTakeover?.id === LUX_PLUS_MONTH_TAKEOVER_ID) return <LuxPlusMonthLandingPageContent />

  return <LuxPlusLandingPageContent />
}

export default LuxPlusLandingPage
